import { AddressBadge } from './AddressBadge';
import { Skeleton } from './ui/skeleton';
import { Medal } from 'lucide-react';
import { Badge } from './ui/badge';

// KROWN Total Supply: 100,000,000,000
const KROWN_TOTAL_SUPPLY = 100_000_000_000;

export function HoldersTable({ holders, loading, symbol = '' }) {
  if (loading) {
    return (
      <div className="table-container rounded-lg border">
        <table className="data-table">
          <thead>
            <tr>
              <th className="w-16">Rank</th>
              <th>Address</th>
              <th className="text-right">Balance</th>
              <th className="text-right">% of Supply</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                <td><Skeleton className="h-6 w-8" /></td>
                <td><Skeleton className="h-6 w-32" /></td>
                <td><Skeleton className="h-6 w-24 ml-auto" /></td>
                <td><Skeleton className="h-6 w-16 ml-auto" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!holders || holders.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No holders found</p>
      </div>
    );
  }

  const getRankBadge = (rank) => {
    if (rank === 1) return <Medal className="w-5 h-5 text-amber-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-700" />;
    return <span className="text-muted-foreground">#{rank}</span>;
  };

  // Calculate percentage of total supply
  const calculatePercentage = (balanceFormatted) => {
    if (!balanceFormatted) return '0.0000';
    
    // Parse the formatted balance (remove commas)
    const balance = parseFloat(balanceFormatted.replace(/,/g, ''));
    if (isNaN(balance)) return '0.0000';
    
    const percentage = (balance / KROWN_TOTAL_SUPPLY) * 100;
    
    // Format based on size
    if (percentage >= 1) {
      return percentage.toFixed(2);
    } else if (percentage >= 0.01) {
      return percentage.toFixed(4);
    } else if (percentage >= 0.0001) {
      return percentage.toFixed(6);
    }
    return '<0.0001';
  };

  // Get badge color based on percentage
  const getPercentageBadgeColor = (percentage) => {
    const pct = parseFloat(percentage);
    if (isNaN(pct)) return 'bg-muted text-muted-foreground';
    if (pct >= 1) return 'bg-amber-500/20 text-amber-600';
    if (pct >= 0.1) return 'bg-emerald-500/20 text-emerald-600';
    if (pct >= 0.01) return 'bg-cyan-500/20 text-cyan-600';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <div className="table-container rounded-lg border">
      <table className="data-table">
        <thead>
          <tr>
            <th className="w-16">Rank</th>
            <th>Address</th>
            <th className="text-right">Balance</th>
            <th className="text-right">% of Supply</th>
          </tr>
        </thead>
        <tbody>
          {holders.map((holder, idx) => {
            const percentage = calculatePercentage(holder.balance_formatted);
            const badgeColor = getPercentageBadgeColor(percentage);
            
            return (
              <tr key={holder.holder_address} className="animate-fade-in" style={{ animationDelay: `${idx * 20}ms` }}>
                <td>
                  <div className="flex items-center justify-center">
                    {getRankBadge(holder.rank)}
                  </div>
                </td>
                <td>
                  <AddressBadge address={holder.holder_address} />
                </td>
                <td className="text-right">
                  <span className="font-mono font-medium">{holder.balance_formatted}</span>
                  {symbol && <span className="text-muted-foreground ml-1 text-sm">{symbol}</span>}
                </td>
                <td className="text-right">
                  <Badge variant="outline" className={`font-mono text-xs ${badgeColor}`}>
                    {percentage}%
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
